import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FolderKanban, MessageSquareQuote, Mail, TrendingUp, SlidersHorizontal, Image, Building, MapPin, Wrench, Settings, ArrowRight, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function SectionCard({ icon: Icon, label, count, description, to, color }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(to)}
      className="bg-[#161923] border border-white/5 rounded-xl p-5 cursor-pointer hover:border-[#FF5A00]/20 hover:bg-[#161923]/80 transition-all group"
      data-testid={`dashboard-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon size={18} className="text-white" />
        </div>
        <ArrowRight size={16} className="text-slate-600 group-hover:text-[#FF5A00] transition-colors" />
      </div>
      <p className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{count}</p>
      <p className="text-sm text-white font-medium mt-1" style={{ fontFamily: "Outfit" }}>{label}</p>
      <p className="text-xs text-slate-500 mt-1">{description}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    slides: 0, gallery: 0, services: 0, projects: 0,
    ongoing: 0, completed: 0, testimonials: 0, messages: 0, mapPins: 0
  });

  useEffect(() => {
    const token = localStorage.getItem("bct_token");
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/api/slides`),
      axios.get(`${API}/api/gallery`),
      axios.get(`${API}/api/services`),
      axios.get(`${API}/api/projects`, { headers }),
      axios.get(`${API}/api/testimonials`, { headers }),
      axios.get(`${API}/api/admin/messages`, { headers, withCredentials: true }),
      axios.get(`${API}/api/map-pins`),
    ]).then(([sl, gl, sv, pr, ts, ms, mp]) => {
      const projects = pr.data;
      setStats({
        slides: sl.data.length,
        gallery: gl.data.length,
        services: sv.data.length,
        projects: projects.length,
        ongoing: projects.filter((x) => x.status === "ongoing").length,
        completed: projects.filter((x) => x.status === "completed").length,
        testimonials: ts.data.length,
        messages: ms.data.length,
        mapPins: mp.data.length,
      });
    }).catch(() => {});
  }, []);

  const sections = [
    { icon: SlidersHorizontal, label: "Slider", count: stats.slides, description: "Anasayfa hero slider resimleri ve yazilari", to: "/admin/slider", color: "bg-blue-600" },
    { icon: Image, label: "Galeri", count: stats.gallery, description: "Anasayfa gorsel galeri bolumu", to: "/admin/gallery", color: "bg-indigo-600" },
    { icon: Building, label: "Hakkimizda", count: "4 Bolum", description: "Kurumsal, Misyon, Vizyon, Istatistikler", to: "/admin/about", color: "bg-teal-600" },
    { icon: Wrench, label: "Hizmetler", count: stats.services, description: "Konut, Alt Yapi, Ticari, Endustriyel kartlari", to: "/admin/services", color: "bg-amber-600" },
    { icon: FolderKanban, label: "Projeler", count: stats.projects, description: `${stats.ongoing} devam eden, ${stats.completed} tamamlanan`, to: "/admin/projects", color: "bg-[#FF5A00]" },
    { icon: MessageSquareQuote, label: "Gorusler", count: stats.testimonials, description: "Musteri gorusleri ve referanslar", to: "/admin/testimonials", color: "bg-emerald-600" },
    { icon: MapPin, label: "Harita Pinleri", count: stats.mapPins, description: "Turkiye haritasi uzerindeki proje pinleri", to: "/admin/map-pins", color: "bg-rose-600" },
    { icon: Mail, label: "Mesajlar", count: stats.messages, description: "Iletisim formu mesajlari", to: "/admin/messages", color: "bg-purple-600" },
    { icon: Settings, label: "Ayarlar", count: "", description: "Sirket adi, telefon, adres, e-posta", to: "/admin/settings", color: "bg-slate-600" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Dashboard</h1>
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-colors" data-testid="view-site-btn">
          <Eye size={14} /> Siteyi Goruntule
        </a>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <div className="bg-gradient-to-br from-[#FF5A00] to-[#FF8A3D] rounded-xl p-4">
          <p className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{stats.projects}</p>
          <p className="text-sm text-white/80 mt-1">Toplam Proje</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl p-4">
          <p className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{stats.ongoing}</p>
          <p className="text-sm text-white/80 mt-1">Devam Eden</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl p-4">
          <p className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{stats.completed}</p>
          <p className="text-sm text-white/80 mt-1">Tamamlanan</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-xl p-4">
          <p className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{stats.messages}</p>
          <p className="text-sm text-white/80 mt-1">Mesaj</p>
        </div>
      </div>

      {/* Section Management Cards */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: "Outfit" }}>Icerik Yonetimi</h2>
        <p className="text-sm text-slate-500">Anasayfadaki tum bolumleri asagidaki kartlardan yonetebilirsiniz</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.map((s) => (
          <SectionCard key={s.label} {...s} />
        ))}
      </div>

      {/* Homepage Mapping Info */}
      <div className="mt-8 bg-[#161923] border border-white/5 rounded-xl p-6">
        <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: "Outfit" }}>Anasayfa Bolum Haritasi</h2>
        <div className="space-y-2 text-sm">
          {[
            ["Hero Slider (Ust banner)", "Slider"],
            ["Gorsel Galeri (Resim kartlari)", "Galeri"],
            ["Hakkimizda Bolumu", "Hakkimizda"],
            ["Hizmetlerimiz (Konut, Altyapi, Ticari, Endustriyel)", "Hizmetler"],
            ["Projelerimiz (Devam Eden / Tamamlanan)", "Projeler"],
            ["Musteri Gorusleri", "Gorusler"],
            ["Turkiye Haritasi Pinleri", "Harita Pinleri"],
            ["Iletisim Bilgileri & Footer", "Ayarlar"],
          ].map(([page, admin], i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
              <span className="text-slate-400 flex-1">{page}</span>
              <span className="text-[#FF5A00] font-medium">{admin}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
